import React, { useState, useContext, useMemo, useCallback } from 'react';

import { ModalBody, VStack, Button, Text, Link } from '@chakra-ui/react';

import { CollectOfferData } from '../../../artifacts/gql/graphql';
// Importing this way to avoid circular dependency
import { NavigationContext } from '../../Navigation/context/NavigationContext';
import { useMonolithUrls } from '../../Navigation/hooks/useMonolithUrls';
import { generateMonolithUrl } from '../../Navigation/utils/monolithUrls';

type SingleOfferModalBodyProps = {
  type: 'seller' | 'buyer' | 'seller-countered';
  viewHandler: (needAwait: boolean, needCloseModal: boolean) => Promise<void>;
  offer: CollectOfferData;
  enableOfferChat: boolean;
};

export const SingleOfferModalBody = ({
  type,
  viewHandler,
  offer,
  enableOfferChat,
}: SingleOfferModalBodyProps) => {
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [
    isRedirectingToOtherNegotiations,
    setIsRedirectingToOtherNegotiations,
  ] = useState(false);
  const { myBuyingUrl, mySellingUrl } = useMonolithUrls();
  const { fanaticsCollectBaseUrl } = useContext(NavigationContext);

  const href = useMemo(() => {
    if (enableOfferChat) {
      return generateMonolithUrl(
        fanaticsCollectBaseUrl ?? '',
        `/fixed/${offer.listingId}?offer=${offer.offerId}`
      );
    }
    if (type === 'buyer') {
      if (offer.listingId) {
        return generateMonolithUrl(
          fanaticsCollectBaseUrl ?? '',
          `/fixed/${offer.listingId}/offer`
        );
      }
      return myBuyingUrl;
    }
    
    if (offer.offerId) {
      return generateMonolithUrl(
        fanaticsCollectBaseUrl ?? '',
        `/fixed/sell/offer/${offer.offerId}`
      );
    }
    return mySellingUrl;
  }, [type, offer.listingId, offer.offerId, enableOfferChat, fanaticsCollectBaseUrl, myBuyingUrl, mySellingUrl]);

  const otherNegotiationsHref = useMemo(() => {
    return type === 'buyer' ? myBuyingUrl : mySellingUrl;
  }, [type, enableOfferChat, offer.listingId, offer.offerId, fanaticsCollectBaseUrl, myBuyingUrl, mySellingUrl]);

  const handleOfferClick = useCallback(async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsRedirecting(true);
    await viewHandler(true, true);
    window.location.assign(href);
  }, [href, viewHandler]);

  const handleNegotiationsClick = useCallback(async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (isRedirectingToOtherNegotiations) return;
    setIsRedirectingToOtherNegotiations(true);
    await viewHandler(true, false);
    window.location.assign(otherNegotiationsHref);
  }, [isRedirectingToOtherNegotiations, viewHandler, otherNegotiationsHref]);

  return (
    <ModalBody padding="0px 24px 24px 24px">
      <VStack spacing={8}>
        <VStack spacing={6}>
          {type === 'seller' && (
            <Text>
              A buyer made an offer on your{' '}
              <strong>{offer.listingTitle || ''}</strong>. Review and respond
              before it expires.
            </Text>
          )}
          {type === 'buyer' && (
            <Text>
              The seller made a counteroffer on their{' '}
              <strong>{offer.listingTitle || ''}</strong>. Review and respond
              before it expires.
            </Text>
          )}
          {type === 'seller-countered' && (
            <Text>
              The buyer made a counteroffer on your{' '}
              <strong>{offer.listingTitle || ''}</strong>. Review and respond
              before it expires.
            </Text>
          )}
          <Text color="#38393C" fontSize="12px" width="100%">
            Access this and other negotiations in{' '}
            <Link
              css={{ textDecoration: 'underline' }}
              href={otherNegotiationsHref}
              onClick={handleNegotiationsClick}
            >
              {type === 'buyer' ? 'My Buying' : 'My Selling'}
            </Link>
            .
          </Text>
        </VStack>
        <Button
          as="a"
          href={href}
          colorScheme="dark"
          isLoading={isRedirecting}
          size="md"
          textTransform="uppercase"
          variant="solid"
          width="100%"
          onClick={handleOfferClick}
        >
          VIEW OFFER
        </Button>
      </VStack>
    </ModalBody>
  );
};
